import { useContext, useState } from "react";
import { AbortError, UnexpectedError, urlBuilderService, ValidationError, ValidationResult } from "sprint-common-web";

import { PublicConfigurationContext } from "../configuration/public/PublicConfigurationContext";
import { AuthContext } from "../security/AuthProvider";

interface UseSprintFetchProps {
    isPublic?: boolean;
}

export const useSprintFetch = (props?: UseSprintFetchProps) => {
    const authContext = useContext(AuthContext);
    const { publicApiKeyValue } = useContext(PublicConfigurationContext);
    const [, setErrorCatch] = useState(null);

    const { buildUrl } = urlBuilderService();

    const authorizedFetch = (url: string, params: RequestInit) => {
        const idToken = authContext.sprintUser.idToken;

        return fetch(url, {
            ...params,
            headers: {
                ...params?.headers,
                Authorization: `Bearer ${idToken}`,
                "Content-Type": "application/json"
            }
        });
    };

    const unauthorizedFetch = (url: string, params: RequestInit) => {
        return fetch(url, {
            ...params,
            headers: {
                ...params?.headers,
                "x-api-key": publicApiKeyValue,
                "Content-Type": "application/json"
            }
        });
    };

    const call = async (
        url: string,
        params: RequestInit,
        fetchFunc: (url: string, params: RequestInit) => Promise<Response>
    ): Promise<Response> => {
        try {
            return await fetchFunc(url, params);
        } catch (error) {
            /* passing `reason` to `signal.abort()` prevents `error.name` property */
            if (error instanceof Error && error.name === "AbortError") {
                throw new AbortError(error.message);
            }

            throw error;
        }
    };

    const sprintFetch = async <T>(url: string, params: RequestInit): Promise<T> => {
        try {
            const response = props?.isPublic
                ? await call(url, params, unauthorizedFetch)
                : await call(url, params, authorizedFetch);

            if (response.ok) {
                return parseToJson<T>(response);
            }

            if (response.status === 400) {
                const validationResult = await parseToJson<ValidationResult>(response);

                throw new ValidationError(validationResult.failures);
            }
            const error = await parseToJson<Error>(response);

            if (isApiError(error)) {
                throw new UnexpectedError(error.message, error.type);
            }

            throw new Error(JSON.stringify(error));
        } catch (error) {
            if (
                !(error instanceof ValidationError || error instanceof AbortError || error instanceof UnexpectedError)
            ) {
                setErrorCatch(() => {
                    throw error;
                });
            }

            throw error;
        }
    };

    const httpGet = async <T>(url: string, query = {}, signal?: AbortSignal): Promise<T> => {
        const fetchParams: RequestInit = { method: "GET", signal };
        const requestUrl = buildUrl(url, query);

        return sprintFetch<T>(requestUrl, fetchParams);
    };

    const httpPost = async <T>(url: string, body = {}, signal?: AbortSignal): Promise<T> => {
        const fetchParams: RequestInit = { method: "POST", body: JSON.stringify(body), signal };

        return sprintFetch<T>(url, fetchParams);
    };

    const httpPut = async <T>(url: string, body = {}, signal?: AbortSignal): Promise<T> => {
        const fetchParams: RequestInit = { method: "PUT", body: JSON.stringify(body), signal };

        return sprintFetch<T>(url, fetchParams);
    };

    const httpDelete = async <T>(url: string, body = {}, signal?: AbortSignal): Promise<T> => {
        const fetchParams: RequestInit = { method: "DELETE", body: JSON.stringify(body), signal };

        return sprintFetch<T>(url, fetchParams);
    };

    return { httpGet, httpPost, httpPut, httpDelete };
};

const parseToJson = async <T>(response: Response): Promise<T> => {
    try {
        return await response.json();
    } catch {
        throw new Error("Response body must not be empty.");
    }
};

function isApiError(error: Error): error is UnexpectedError {
    return (error as UnexpectedError).type !== "UnexpectedError";
}
